<script setup lang="ts">
import { RouterLink } from "vue-router";
import { Platform, PlatformKey } from "../services/cmt";
import { useUserStore } from "@/store/user";
import { useApplicationStore } from "@/store/application";
import { TransitionRoot } from "@headlessui/vue";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const route = useRoute();
const userStore = useUserStore();
const applicationStore = useApplicationStore();
const isInitialized = computed(() => applicationStore.isInitialized);
const pages = computed(() => applicationStore.pages);
const windowSize = ref(window.innerWidth);
const isNavBarExpanded = ref(windowSize.value > 1200);

const { isTrialVersion } = useFeatureFlag();

let resizeObserver: ResizeObserver | null = null;

const updateWindowSize = (entries: ResizeObserverEntry[]) => {
  for (const entry of entries) {
    windowSize.value = entry.contentRect.width;
  }
};

const isUserAdmin = computed(() => userStore.isAdmin);
const isElevatedUser = computed(() => userStore.isElevatedUser);

const cmtPlatforms = computed(() => applicationStore.cmtPlatforms);
const platforms = computed<Platform[] | null>(() => {
  if (!cmtPlatforms.value) return null;
  if (!pages.value?.length) return null;
  return cmtPlatforms.value.filter(
    (platform) => applicationStore.platforms?.includes(platform.key),
  );
});

const isSearchFolder = computed(() => route.path.includes("/search"));

const navbarSetsize = computed(() => {
  if (!platforms.value) {
    if (!isUserAdmin.value) return 1;
    return 1;
  } else if (!isUserAdmin.value) return platforms.value.length + 2;

  return platforms.value.length + 3;
});

onMounted(async () => {
  await applicationStore.initializeApplication();
  resizeObserver = new ResizeObserver(updateWindowSize);
  resizeObserver.observe(document.documentElement);
});

watch(windowSize, () => {
  isNavBarExpanded.value = windowSize.value >= 1200;
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<template>
  <div class="relative">
    <button
      v-if="isInitialized"
      class="absolute -right-3.5 top-2 z-100 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md"
      @click="isNavBarExpanded = !isNavBarExpanded"
      :aria-label="isNavBarExpanded ? $t('general.collapse_menu') : $t('general.expand_menu')"
      :aria-expanded="isNavBarExpanded"
    >
      <IconExpandMenu :class="{ 'rotate-180': !isNavBarExpanded }" />
    </button>
    <aside
      class="custom-scrollbar relative flex h-screen w-[115px] shrink-0 flex-col overflow-y-auto border-r p-4 text-ppa-inactive"
      :class="{
        'overflow-y-hidden': isSearchFolder,
        'w-[350px] p-6 ': isNavBarExpanded,
      }"
    >
      <nav
        role="tree"
        :aria-label="$t('general.navigation')"
        :class="{ 'pointer-events-none': isSearchFolder }"
        :inert="isSearchFolder"
      >
        <div
          class="group mb-6 flex h-10 w-full cursor-pointer justify-between font-extrabold tracking-widest"
        >
          <router-link
            to="/dashboard"
            class="flex w-full justify-between"
            aria-current="page"
            role="treeitem"
            aria-posinset="1"
            :aria-setsize="navbarSetsize"
            tabindex="0"
          >
            <NavBarLabel
              :label="$t('navbar.dashboard')"
              :aria-label="$t('navbar.dashboard')"
              iconName="home"
              :active="route.path === '/dashboard'"
              :isNavbarExpanded="isNavBarExpanded"
              :isUppercase="true"
              role="none"
            />
          </router-link>
        </div>
        <div
          v-if="isElevatedUser"
          class="group mb-6 flex h-10 w-full cursor-pointer justify-between font-extrabold tracking-widest"
        >
          <router-link
            to="/admin"
            class="flex w-full justify-between"
            aria-current="page"
            role="treeitem"
            aria-posinset="2"
            :aria-setsize="navbarSetsize"
            tabindex="0"
          >
            <NavBarLabel
              :label="$t('navbar.admin_section')"
              :aria-label="$t('navbar.admin_section')"
              iconName="admin"
              :active="route.path === '/admin'"
              :isNavbarExpanded="isNavBarExpanded"
              :isUppercase="true"
              role="none"
            />
          </router-link>
        </div>
        <div
          class="group mb-6 flex h-10 w-full cursor-pointer justify-between font-extrabold tracking-widest"
        >
          <router-link
            v-if="!isTrialVersion"
            to="/statistics"
            class="flex w-full justify-between"
            aria-current="page"
            role="treeitem"
            aria-posinset="3"
            :aria-setsize="navbarSetsize"
            tabindex="0"
          >
            <NavBarLabel
              :label="$t('navbar.statistics')"
              :aria-label="$t('navbar.statistics')"
              iconName="statistics"
              :active="route.path === '/statistics'"
              :isNavbarExpanded="isNavBarExpanded"
              :isUppercase="true"
              role="none"
            />
          </router-link>
          <div v-else
            class="opacity-50 cursor-not-allowed"
          >
            <NavBarLabel
              :label="$t('navbar.statistics')"
              :aria-label="$t('navbar.statistics')"
              iconName="statistics"
              :active="route.path === '/statistics'"
              :isNavbarExpanded="isNavBarExpanded"
              :isUppercase="true"
              role="none"
            />
          </div>
        </div>
        <template v-if="platforms" v-for="(platform, index) in platforms" :key="platform.key">
          <NavBarPlatformMenu
            :platform="platform"
            :aria-posinset="index + 3"
            :aria-setsize="navbarSetsize"
            :isNavbarExpanded="isNavBarExpanded"
          />
        </template>
      </nav>
      <TransitionRoot
        :show="isSearchFolder"
        enter="duration-200 ease-in-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in-out"
        leave-from="opacity-100"
        leave-to="opacity-0"
        as="template"
      >
        <div
          class="absolute inset-0 flex select-none flex-col items-center justify-center bg-ppa-navbar-overlay backdrop-blur-sm"
        >
          <div class="flex translate-y-[-10vh] flex-col items-center justify-center">
            <IllustrationLoupe></IllustrationLoupe>
            <div class="mb-6 mt-4 text-ppa-default font-semibold text-ppa-dark-blue">
              {{ $t("search.navbar_search_mode_explanation") }}
            </div>
          </div>
        </div>
      </TransitionRoot>
    </aside>
  </div>
</template>
<style scoped>
.router-link-active {
  font-weight: 800;
  color: #042b5f;
}
</style>
